import MnuObj from '../MnuObj';
import MnuMgr from '../MnuMgr';
// import { BTN_HGT } from '../MnuObj';
import { SEP_HGT } from '../MnuItm';
import Color from '../../../utils/Color';
import DomEventHelper from '../../../utils/DomEventHelper';
import EventListenerManager from '../../../utils/EventListenerManager';
import HtmHelper from '../../../utils/HtmHelper';
import Validator from '../../../utils/Validator';
import SearchInputMenuItem from './SearchInputMenuItem';
import { FIXED_AREA_SEPARATOR_COLOR, FIXED_AREA_SEPARATOR_HEIGHT } from './SearchInputMenuItem';

export default class SearchableMenuObject extends MnuObj {

	/**
	 * constructs a new instance
	 * @param {MnuMgr} menuManager the menu manager
	 * @param {Number} menuId menu ID
	 * @param {Array} menuItemsDescriptors the menu item descriptors
	 */
	constructor( menuManager, menuId, menuItemsDescriptors ) {
		super( menuManager, null, menuId, menuItemsDescriptors );
		this.attachInputDisplay();
	}

	get searchDisplayText() {
		if ( !Validator.isObject( this.searchInputMenuItem ) ||
			!this.searchInputMenuItem.isRendered ) {
			return "";
		}
		return this.searchInputMenuItem.value;
	}

	set searchDisplayText( newText ) {
		if ( !Validator.isObject( this.searchInputMenuItem ) ||
			!this.searchInputMenuItem.isRendered ) {
			return;
		}
		this.searchInputMenuItem.value = newText;
	}

	get searchString() {
		const searchString = this.searchDisplayText;
		return !Validator.isString( searchString ) ? "" :
			searchString.trimStart().replace( /\.+/g, "." )
			.replace( /\s+/g, " " ).toLowerCase();
	}

	get searchInputContainerElement() {
		if ( !Validator.isObject( this.searchInputMenuItem ) ||
			!this.searchInputMenuItem.isRendered ) {
			return void 0;
		}
		return this.searchInputMenuItem.container;
	}

	get itemHostRect() {
		if ( !( this.itmHost instanceof HTMLElement ) ) {
			return void 0;
		}
		return this.itmHost.getBoundingClientRect();
	}

	get itemHostHeight() {
		const itemHostRect = this.itemHostRect;
		if ( !Validator.isObject( itemHostRect ) ) {
			return void 0;
		}
		return itemHostRect.height;
	}

	get documentBodyRect() {
		return window.document.body.getBoundingClientRect();
	}

	get documentBodyHeight() {
		return this.documentBodyRect.height;
	}

	get searchInputContainerElementRect() {
		const searchInputContainerElement = this.searchInputContainerElement;
		if ( !( searchInputContainerElement instanceof HTMLElement ) ) {
			return void 0;
		}
		return searchInputContainerElement.getBoundingClientRect();
	}

	get searchInputContainerElementHeight() {
		const searchInputContainerElementRect = this.searchInputContainerElementRect;
		if ( !Validator.isObject( searchInputContainerElementRect ) ) {
			return void 0;
		}
		return searchInputContainerElementRect.height;
	}

	get noScrollMenuHeight() {
		const itemHostHeight = this.itemHostHeight;
		if ( !Validator.isPositiveNumber( itemHostHeight ) ) {
			return void 0;
		}
		const searchInputContainerElementHeight =
			this.searchInputContainerElementHeight || 0;
		return itemHostHeight + searchInputContainerElementHeight;
	}

	/**
	 * @override
	 */
	show() {
		const originalReturnValue = super.show();
		if ( this.btnDwn instanceof HTMLElement ) {
			this.btnDwn.style.borderTop = `${ FIXED_AREA_SEPARATOR_HEIGHT }px solid` +
				` ${ FIXED_AREA_SEPARATOR_COLOR }`;;
		}
		this.attachKeyListener();
		this.focusMenu();
		return originalReturnValue;
	}

	/**
	 * installs a key listener on the menu element so the search input receives
	 * printable keystrokes; the search input itself is a passive display that
	 * suppresses native input, so the actual "type-to-filter" is driven from here
	 * @returns {Boolean} true if the listener was installed
	 */
	attachKeyListener() {
		if ( !( this.mnuDiv instanceof HTMLElement ) ) {
			return false;
		}
		this.removeKeyListener();
		const keyUpAdded = EventListenerManager.addListener( {
			instance: this,
			eventName: "keyup",
			functionName: "onMenuKeyUp",
			callBackPrefix: "SearchableMenu",
			element: this.mnuDiv,
			useCapture: false
		} );
		const keyDownAdded = EventListenerManager.addListener( {
			instance: this,
			eventName: "keydown",
			functionName: "onMenuKeyDown",
			callBackPrefix: "SearchableMenu",
			element: this.mnuDiv,
			useCapture: false
		} );
		return keyUpAdded && keyDownAdded;
	}

	removeKeyListener() {
		if ( !( this.mnuDiv instanceof HTMLElement ) ) {
			return false;
		}
		EventListenerManager.removeListener( this, "keyup", this.mnuDiv, "SearchableMenu" );
		EventListenerManager.removeListener( this, "keydown", this.mnuDiv, "SearchableMenu" );
		return true;
	}

	/**
	 * makes the menu element focusable and focuses it so keystrokes are captured
	 * @returns {Boolean} true if the menu element was focused
	 */
	focusMenu() {
		if ( !( this.mnuDiv instanceof HTMLElement ) ) {
			return false;
		}
		this.mnuDiv.tabIndex = 0;
		this.mnuDiv.focus();
		return true;
	}

	/**
	 * key listener callback: handles navigation keys and routes printable keys /
	 * [Backspace] into the search input
	 * @param {KeyboardEvent} domEvent the keyboard event
	 * @returns {Boolean} true if the event was handled
	 */
	onMenuKeyUp( domEvent ) {
		// a hosting handler (e.g. the table header) may have processed this event already
		if ( DomEventHelper.isProcessed( domEvent ) ) {
			return false;
		}
		if ( this.handleNavigationKey( domEvent ) ) {
			DomEventHelper.stopIf( domEvent );
			return true;
		}
		if ( !DomEventHelper.isPrintableKey( domEvent ) &&
			!DomEventHelper.keyIs( domEvent, "Backspace" ) ) {
			return false;
		}
		const handled = this.onSearchableMenuPrintableKey( domEvent );
		if ( handled ) {
			DomEventHelper.stopIf( domEvent );
		}
		return handled;
	}

	/**
	 * key listener callback: prevents navigation keys from reaching a hosting widget
	 * (e.g. the table body) so they don't cause navigation outside the menu
	 * @param {KeyboardEvent} domEvent the keyboard event
	 * @returns {Boolean} true if the event was suppressed
	 */
	onMenuKeyDown( domEvent ) {
		if ( DomEventHelper.isProcessed( domEvent ) ) {
			return false;
		}
		// suppress navigation keys (so they don't navigate a hosting widget) and
		// [Backspace] (which is consumed as "delete filter char" and must never
		// escape the menu - a stray Backspace could otherwise flip the dialog or
		// browser history)
		if ( this.isNavigationKey( domEvent ) ||
			DomEventHelper.keyIs( domEvent, "Backspace" ) ) {
			DomEventHelper.stopIf( domEvent );
			return true;
		}
		return false;
	}

	/**
	 * checks whether a keyboard event is one of the menu navigation keys
	 * @param {KeyboardEvent} domEvent the keyboard event
	 * @returns {Boolean} true if the event refers to a navigation key
	 */
	isNavigationKey( domEvent ) {
		return DomEventHelper.keyIs( domEvent, "Home" ) ||
			DomEventHelper.keyIs( domEvent, "End" ) ||
			DomEventHelper.keyIs( domEvent, "Enter" ) ||
			DomEventHelper.isPageUp( domEvent ) ||
			DomEventHelper.isPageDown( domEvent ) ||
			DomEventHelper.isArrowUp( domEvent ) ||
			DomEventHelper.isArrowDown( domEvent );
	}

	/**
	 * dispatches a navigation key to the matching handler
	 * @param {KeyboardEvent} domEvent the keyboard event
	 * @returns {Boolean} true if the event was a handled navigation key
	 */
	handleNavigationKey( domEvent ) {
		if ( DomEventHelper.keyIs( domEvent, "Enter" ) ) {
			return this.activateFocusedItem();
		}
		if ( DomEventHelper.keyIs( domEvent, "Home" ) ) {
			return this.focusFirstItem();
		}
		if ( DomEventHelper.keyIs( domEvent, "End" ) ) {
			return this.focusLastItem();
		}
		if ( DomEventHelper.isPageUp( domEvent ) || DomEventHelper.isArrowUp( domEvent ) ) {
			return this.focusPreviousItem();
		}
		if ( DomEventHelper.isPageDown( domEvent ) || DomEventHelper.isArrowDown( domEvent ) ) {
			return this.focusNextItem();
		}
		return false;
	}

	/**
	 * @returns {Number} the index of the currently focused item within the visible items, or -1
	 */
	get focusedItemIndex() {
		const items = this.visibleMenuItems;
		return Validator.isArray( items, true ) ? items.findIndex( item => item.focused ) : -1;
	}

	/**
	 * focuses a visible menu item by index and scrolls it into view
	 * @param {Number} index the target index within the visible items
	 * @param {Boolean} up direction hint for scrolling
	 * @returns {Boolean} true if an item was focused
	 */
	focusItemAtIndex( index, up = true ) {
		const items = this.visibleMenuItems;
		if ( !Validator.isArray( items, true ) || index < 0 || index >= items.length ) {
			return false;
		}
		const target = items[ index ];
		if ( !Validator.isObject( target ) ) {
			return false;
		}
		this.clearFocusedItem();
		target.focused = true;
		this.scrollItemIntoView( target, up );
		// keep DOM focus on the menu element so subsequent keystrokes are captured;
		// navigation may have been triggered from the search input, which blurs itself
		this.focusMenu();
		return true;
	}

	focusFirstItem() {
		return this.focusItemAtIndex( 0, true );
	}

	focusLastItem() {
		const items = this.visibleMenuItems;
		return Validator.isArray( items, true ) ?
			this.focusItemAtIndex( items.length - 1, false ) : false;
	}

	focusNextItem() {
		const items = this.visibleMenuItems;
		if ( !Validator.isArray( items, true ) ) {
			return false;
		}
		const current = this.focusedItemIndex;
		const next = current < 0 ? 0 : Math.min( current + 1, items.length - 1 );
		return this.focusItemAtIndex( next, false );
	}

	focusPreviousItem() {
		const items = this.visibleMenuItems;
		if ( !Validator.isArray( items, true ) ) {
			return false;
		}
		const current = this.focusedItemIndex;
		// from the first item (or none) upwards, return the focus to the search input
		if ( current <= 0 ) {
			this.clearFocusedItem();
			return this.focusSearchInput();
		}
		return this.focusItemAtIndex( current - 1, true );
	}

	/**
	 * activates ([Enter]) the currently focused item
	 * @returns {Boolean} true if an item was activated
	 */
	activateFocusedItem() {
		const items = this.visibleMenuItems;
		if ( !Validator.isArray( items, true ) ) {
			return false;
		}
		const focusedItem = items.find( item => item.focused );
		if ( !Validator.isFunctionPath( focusedItem, "focusedItem.activate" ) ) {
			return false;
		}
		return focusedItem.activate();
	}

	refreshInputPlaceholder() {
		return Validator.isFunctionPath( this.searchInputMenuItem,
				"searchInputMenuItem.refreshInputPlaceholder" ) ?
			this.searchInputMenuItem.refreshInputPlaceholder() : void 0;
	}

	addToSearchDisplayText( additionText ) {
		if ( !Validator.isString( additionText ) ) {
			return false;
		}
		this.searchDisplayText = this.searchDisplayText + additionText;
		return true;
	}

	removeLastCharacterFromSearchDisplayText() {
		const currentSearchDisplayText = this.searchDisplayText;
		if ( !Validator.isString( currentSearchDisplayText ) ) {
			return false;
		}
		this.searchDisplayText = currentSearchDisplayText
			.substring( 0, currentSearchDisplayText.length - 1 );
		return true;
	}

	/**
	 * @override
	 */
	setVisHgt( visibleHeightInPixels ) {
		const returnValue = super.setVisHgt( visibleHeightInPixels );
		if ( !Validator.isObject( this.searchInputMenuItem ) ) {
			return returnValue;
		}
		const was_visible = this.searchInputMenuItem.isVisible;
		const requiredHeightInPixels = this.getMnuHgt();
		requiredHeightInPixels <= visibleHeightInPixels ? this.searchInputMenuItem.hide() : this.searchInputMenuItem.show();
		const is_visible = this.searchInputMenuItem.isVisible;
		if ( is_visible !== was_visible ) {
			const height = this.searchInputMenuItem.height;
			this.scrHeight = Math.max(0, this.scrHeight + (is_visible ? -height : height));
		}
		return returnValue;
	}

	get searchInputMenuItemIsVisible() {
		return !Validator.isObject( this.searchInputMenuItem ) ? false :
			this.searchInputMenuItem.isVisible;
	}

	/**
	 * destructor method
	 * @override
	 */
	doDestroy() {
		this.removeKeyListener();
		this.discardInputUi();
		super.doDestroy();
	}

	attachInputDisplay() {
		this.discardInputUi();
		if ( !( this.mnuDiv instanceof HTMLElement ) ||
			!( this.scrHost instanceof HTMLElement ) ) {
			return false;
		}
		new SearchInputMenuItem( this );
		if ( !this.searchInputMenuItem.isRendered ) {
			return false;
		}
		const searchContainer = this.searchInputMenuItem.container;
		this.mnuDiv.insertBefore( searchContainer, this.scrHost );
		this.removeButtonBorder();
		return true;
	}

	removeButtonBorder() {
		if ( !( this.btnUp instanceof HTMLElement ) ) {
			return false;
		}
		this.btnUp.style.borderBottom = "0px solid rgba(255,255,255,0)";
		return true;
	}

	discardInputUi() {
		if ( Validator.isFunctionPath(
				this.searchInputMenuItem, "searchInputMenuItem.removeSelf" ) ) {
			return this.searchInputMenuItem.removeSelf();
		}
		return false;
	}

	get searchInputPlaceholder() {
		return Validator.isObjectPath( this.mnuMgr, "mnuMgr.curHdl" ) &&
			Validator.isString( this.mnuMgr.curHdl.searchInputPlaceholder ) ?
			this.mnuMgr.curHdl.searchInputPlaceholder : void 0;
	}

	get functionalMenuItems() {
		if ( !Validator.isObject( this.mnuItems ) ||
			!Validator.isMap( this.mnuItems._objReg ) ) {
			return void 0;
		}
		return [ ...this.mnuItems._objReg.values() ].filter( item =>
			Validator.is( item, "MnuItm" ) && item.alive && item.enabled &&
			!item.inactive && !item.sep && Validator.isPositiveInteger( item.id ) );
	}

	get visibleMenuItems() {
		if ( !Validator.isObject( this.mnuItems ) ||
			!Validator.isMap( this.mnuItems._objReg ) ) {
			return void 0;
		}
		return [ ...this.mnuItems._objReg.values() ].filter( item =>
			Validator.is( item, "MnuItm" ) && !item.inactive && !item.sep &&
			item.element instanceof HTMLElement &&
			!item.element.classList.contains( "invisible" ) );
	}

	hasSearchString( menuItem, startsWithPoint, searchString ) {
		if ( !Validator.isObject( menuItem ) ||
			!( menuItem.spanText instanceof HTMLElement ) ) {
			return true;
		}
		const menuItemTitle = menuItem.spanText.innerHTML.toLowerCase().trim();
		return !!startsWithPoint ? menuItemTitle.indexOf( searchString ) >= 0 :
			menuItemTitle.startsWith( searchString ) ||
			menuItemTitle.indexOf( "." + searchString ) >= 0;
	}

	focusSearchInput() {
		return Validator.isFunctionPath(
				this.searchInputMenuItem, "searchInputMenuItem.focus" ) ?
			this.searchInputMenuItem.focus() : false;
	}

	reactToInputChange() {
		// TODO kind of redundant code
		const textMenuSearched = this.searchContextMenu();
		const menuUiAdjusted = this.adjustMenuUiAfterSearch();
		return textMenuSearched && menuUiAdjusted;
	}

	onSearchableMenuPrintableKey( domEvent ) {
		if ( !Validator.isObject( domEvent ) || !this.searchInputMenuItemIsVisible ) {
			return false;
		}
		const isBackspace = DomEventHelper.keyIs( domEvent, "Backspace" );
		if ( this.allMenuItemsAreHidden && !isBackspace ) {
			return false;
		}
		DomEventHelper.stopIf( domEvent );
		this.focusSearchInput();
		const displayTextChanged = isBackspace ?
			this.removeLastCharacterFromSearchDisplayText() :
			this.addToSearchDisplayText( domEvent.key );
		const textSearchedAndMenuAdjusted = this.reactToInputChange();
		return displayTextChanged && textSearchedAndMenuAdjusted;
	}

	searchContextMenu() {
		const functionalMenuItems = this.functionalMenuItems;
		if ( !Validator.isIterable( functionalMenuItems ) ) {
			return false;
		}
		const searchString = this.searchString;
		const searchStringStartsWithPoint = searchString.startsWith( "." );
		this.allMenuItemsAreHidden = true;
		for ( let menuItem of functionalMenuItems ) {
			const showItem = this.hasSearchString(
				menuItem, searchStringStartsWithPoint, searchString );
			if ( !showItem ) {
				this.temporaryHideMenuItem( menuItem );
				continue;
			}
			this.unhideMenuItem( menuItem );
			this.allMenuItemsAreHidden = false;
		}
		return true;
	}

	adjustMenuUiAfterSearch() {
		const necessaryHeight = this.noScrollMenuHeight;
		if ( !Validator.isPositiveNumber( necessaryHeight ) ) {
			return false;
		}
		const bodyHeight = this.documentBodyHeight;
		this._adjustMenuUiAfterSearch( necessaryHeight > bodyHeight );
		return true;
	}

	_adjustMenuUiAfterSearch( needsScrolling = true ) {
		this.canScroll = !!needsScrolling;
		if ( this.mnuDiv instanceof HTMLElement ) {
			this.mnuDiv.style.top = `0px`;
			!!needsScrolling ? this.mnuDiv.classList.remove( "searched" ) :
				this.mnuDiv.classList.add( "searched" );
		}
		if ( this.btnUp instanceof HTMLElement ) {
			this.btnUp.style.display = !!needsScrolling ? "flex" : "none";
		}
		if ( this.btnDwn instanceof HTMLElement ) {
			this.btnDwn.style.display = !!needsScrolling ? "flex" : "none";
		}
		if ( this.itmHost instanceof HTMLElement ) {
			this.itmHost.style.top = "0px";
		}
		return !!needsScrolling ? true : this._adjustMnuTopAfterSearch();
	}

	_adjustMnuTopAfterSearch() {
		if ( !Validator.isObject( this.mnuMgr ) ||
			!( this.mnuDiv instanceof HTMLElement ) ||
			!( this.mnuMgr.originElement instanceof HTMLElement ) ) {
			return false;
		}
		const necessaryHeight = this.noScrollMenuHeight;
		if ( !Validator.isPositiveNumber( necessaryHeight ) ) {
			return false;
		}
		const originElementRect = this.mnuMgr.originElement.getBoundingClientRect();
		const originElementBottom = originElementRect.top + originElementRect.height;
		const bodyHeight = this.documentBodyHeight;
		const availableBottomSpace = bodyHeight - originElementBottom;
		const menuTop = necessaryHeight < availableBottomSpace ?
			originElementBottom : bodyHeight - necessaryHeight;
		this.mnuDiv.style.top = `${ menuTop }px`;
		return true;
	}

	_setMenuItemSearchTemporaryVisibility( menuItem, setVisible = true ) {
		if ( !Validator.isObject( menuItem ) ||
			!( menuItem.element instanceof HTMLElement ) ) {
			return false;
		}
		menuItem.temporaryHidden = !!setVisible;
		menuItem.element.classList.add( "menu-item" );
		!!setVisible ? menuItem.element.classList.remove( "invisible" ) :
			menuItem.element.classList.add( "invisible" );
		return true;
	}

	temporaryHideMenuItem( menuItem ) {
		return this._setMenuItemSearchTemporaryVisibility( menuItem, false );
	}

	unhideMenuItem( menuItem ) {
		// the name "showMenuItem" might be misinterpreted
		return this._setMenuItemSearchTemporaryVisibility( menuItem, true );
	}

}

// register with the menu manager so it can create searchable menus without a static
// MnuMgr -> SearchableMenuObject import (which would form an import cycle through MnuObj
// and risk a "MnuObj is not initialized" temporal-dead-zone error); see MnuMgr.registerSearchableMenuClass
MnuMgr.registerSearchableMenuClass( SearchableMenuObject );
